import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import FilterOption from "@/features/sub-families/components/filter-option.tsx";
import AccessoriesFilter from "@/features/sub-families/components/filters/accessories-filter.tsx";
import NoFilterOptions from "@/features/sub-families/components/filters/internal/no-filter-options.tsx";
import MountingKitsFilter from "@/features/sub-families/components/filters/mounting-kits-filter.tsx";
import useGetSelectedOptionLabel from "@/features/sub-families/hooks/use-get-selected-option-label.ts";
import useMountingKits from "@/features/sub-families/hooks/use-mounting-kits.ts";
import usePowerSupplyDimmingType from "@/features/sub-families/hooks/use-power-supply-dimming-type.ts";
import { useChangeFilterOption } from "@/features/sub-families/providers/change-filter-option-provider.tsx";
import { useTranslations } from "@/features/translations/components/translations.tsx";
import useCurrentPowerSupply from "@/services/product/hooks/use-current-power-supply.ts";
import useCurrentProduct from "@/services/product/hooks/use-current-product.ts";
import { typedObjectEntries } from "@/utils/types.ts";
import { useSubFamiliesConfig } from "../providers/sub-families-config-provider.tsx";
import { ConfigurationTab, type ConfigurationTabEnum } from "../types";
import FinishingColoursFilter from "./filters/finishing-colours-filter.tsx";
import SubFamiliesFilter from "./filters/sub-families-filter.tsx";

type Props = { tab: ConfigurationTabEnum };
export default function SubFamilyFilterOptions({ tab }: Props) {
    const t = useTranslations();

    const currentProduct = useCurrentProduct();
    const mountingKits = useMountingKits();

    const { filterOptions } = useSubFamiliesConfig();

    const getSelectedOptionLabel = useGetSelectedOptionLabel();

    const { changeFilterOption } = useChangeFilterOption();

    const selectedPowerSupply = useCurrentPowerSupply();
    const powerSupplyDimmingType = usePowerSupplyDimmingType();
    if (!powerSupplyDimmingType) return null;

    const {
        powerSupplyOption,
        dimmingTypeOption,
        selectedDimmingTypeLabel,
        selectedPowerSupplyConfigCode,
        selectedDimmingTypeConfigCode,
        displayAlert,
        onChangePowerSupply,
        onChangeDimmingType,
    } = powerSupplyDimmingType;

    const isDesign = tab === ConfigurationTab.Design;
    const isLight = tab === ConfigurationTab.Light;
    const isAccessory = tab === ConfigurationTab.Accessory;
    const options = filterOptions ? typedObjectEntries(filterOptions).filter(([, config]) => config.tab === tab) : [];

    if (isLight && options.length === 0) return <NoFilterOptions />;

    let nextIndex = options.length;
    const mountingKitsIdx = mountingKits ? ++nextIndex : nextIndex;
    const finishingColoursIdx = ++nextIndex;

    return (
        <>
            {/* Fixed filters at the top */}
            {isDesign && <SubFamiliesFilter />}

            {/* Dynamic filters */}
            {options.map(([productProperty, config], idx) => {
                return (
                    <FilterOption
                        key={productProperty}
                        productProperty={productProperty}
                        isDesign={isDesign}
                        idx={isDesign ? idx + 1 : idx} // Because we have a fixed filter at the top for design, offset design filters by 1
                        config={config}
                        selectedLabel={getSelectedOptionLabel(productProperty)}
                        onChange={(value) => changeFilterOption(value.descriptor, value.configCode)}
                    />
                );
            })}

            {/* Fixed filters at the bottom */}
            {isDesign && (
                <>
                    {currentProduct && mountingKitsIdx !== undefined && (
                        <MountingKitsFilter
                            idx={mountingKitsIdx}
                            selectedLabel={getSelectedOptionLabel("ceiling_meta_data")}
                            onChange={(value) => changeFilterOption(value.descriptor, value.configCode)}
                        />
                    )}
                    <FinishingColoursFilter idx={finishingColoursIdx} />
                </>
            )}
            {isLight && powerSupplyOption !== null && (
                <FilterOption
                    productProperty="power_supply"
                    config={powerSupplyOption}
                    selectedLabel={selectedPowerSupply?.product_label ?? null}
                    selectedConfigCode={selectedPowerSupplyConfigCode}
                    onChange={({ configCode, isPossible }) => onChangePowerSupply?.(configCode, isPossible)}
                >
                    {displayAlert && (
                        <Alert>
                            <AlertDescription>{t("power_supply_label")}</AlertDescription>
                        </Alert>
                    )}
                </FilterOption>
            )}
            {isLight && dimmingTypeOption !== null && (
                <FilterOption
                    productProperty="dimming_type"
                    config={dimmingTypeOption}
                    selectedLabel={selectedDimmingTypeLabel}
                    selectedConfigCode={selectedDimmingTypeConfigCode}
                    onChange={({ configCode, isPossible }) => onChangeDimmingType?.(configCode, isPossible)}
                />
            )}
            {isAccessory && (
                <AccessoriesFilter
                    selectedLabel={getSelectedOptionLabel("light_acc")}
                    onChange={(value) => changeFilterOption(value.descriptor, value.configCode)}
                />
            )}
        </>
    );
}
